/**
 * STJ Advisors brand style configuration — colors, typography, and prompt
 * rules extracted from the client's "STJ Slides to Ingest.pptx" (slide master
 * colorScheme "STJ Advisors - Colour Theme" + fontScheme "STJ Advisors - Font
 * Theme", layout placeholder geometry, via the OOXML).
 *
 * Mirrors the shape of `cavendishStyleConfig.ts` / `evercoreStyleConfig.ts`.
 * When STJ style is active the prompt below is appended to the system prompt
 * and the color/theme constants are used as renderer defaults.
 *
 * Fidelity note: colors come from the master-linked colorScheme; fonts from
 * fontScheme (Nexa Bold titles, NexaRegular body — Nexa Light is used on the
 * subtitle placeholder). Slide size is 16:9 widescreen (960×540 pt). Title /
 * subtitle / footnote geometry is from the "2_Title Slide" layout (the
 * master's first layout, so default AddSlide already uses it). The logo lives
 * on the slide master (EMF) and is repeated on the title layouts, so no
 * contentLayoutName is required. Designer RGB chips on those layouts sit
 * off-slide (negative Y) and are not part of the visible content canvas.
 * Template charts store almost no explicit srgb fills, so the series palette
 * is derived from the theme accents (navy-led).
 */

import type { ThemeColors } from "../../types";

// ---------------------------------------------------------------------------
// Color palette (6-digit hex, no '#') — from "STJ Advisors - Colour Theme"
// ---------------------------------------------------------------------------

export const STJ_COLORS = {
  navy: "022C6A", // accent1 — STJ signature navy (primary brand / emphasis)
  teal: "016C64", // dk2 — STJ teal
  paleTeal: "B2D3D0", // lt2
  paleNavy: "CCD5E1", // accent2
  orange: "E48439", // accent3
  paleOrange: "F9E4D3", // accent4

  ink: "000000", // dk1 / accent6 — titles & body text (black)
  white: "FFFFFF", // lt1 / accent5

  ruleGray: "3A3A3A", // observed slide gray for rules / separators
  altRow: "E8EAF0", // light navy-tint for alternate-row table shading
} as const;

/**
 * Drop-in ThemeColors override when STJ style is active. Values map 1:1 to
 * the STJ slide-master colorScheme + fontScheme.
 */
export const STJ_THEME_COLORS: ThemeColors = {
  dark1: STJ_COLORS.ink, // #000000 — Text1
  dark2: STJ_COLORS.teal, // #016C64 — Text2
  light1: STJ_COLORS.white, // #FFFFFF — Background1
  light2: STJ_COLORS.paleTeal, // #B2D3D0 — Background2
  accent1: STJ_COLORS.navy, // #022C6A
  accent2: STJ_COLORS.paleNavy, // #CCD5E1
  accent3: STJ_COLORS.orange, // #E48439
  accent4: STJ_COLORS.paleOrange, // #F9E4D3
  accent5: STJ_COLORS.white, // #FFFFFF
  accent6: STJ_COLORS.ink, // #000000
  hyperlink: STJ_COLORS.white, // scheme hlink is white
  followedHyperlink: STJ_COLORS.teal,
  majorFont: "Nexa Bold",
  minorFont: "NexaRegular",
};

/**
 * Ordered series palette. Template charts have almost no explicit fills, so
 * this is derived from the theme, led by STJ navy (accent1), then orange and
 * teal for readable multi-series separation.
 */
export const STJ_CHART_SERIES_PALETTE = [
  STJ_COLORS.navy,
  STJ_COLORS.orange,
  STJ_COLORS.teal,
  STJ_COLORS.paleNavy,
  STJ_COLORS.paleTeal,
  STJ_COLORS.ruleGray,
];

// ---------------------------------------------------------------------------
// Slide dimensions & placeholder positions (from the STJ "2_Title Slide" layout)
// ---------------------------------------------------------------------------

export const STJ_SLIDE_DIMENSIONS = { width: 960, height: 540 } as const;

export const STJ_POSITIONS = {
  title: { left: 47.2, top: 16.1, width: 893.6, height: 33.8 },
  contentStart: { left: 47.2, top: 63.8 },
  footnote: { left: 41.2, top: 508.8, width: 877.5, height: 20.0 },
} as const;

// Authoritative layout names from the STJ master.
export const STJ_LAYOUT_NAMES = [
  "2_Title Slide",
  "3_Title Slide",
  "1_Title Slide",
  "5_Title Slide",
  "4_Title Slide",
] as const;

// ---------------------------------------------------------------------------
// Prompt text — appended to the system prompt when STJ style is active
// ---------------------------------------------------------------------------

export const STJ_STYLE_PROMPT = `

## STJ ADVISORS BRAND STYLE OVERRIDE (active — apply these rules to ALL output)

REFERENCE PRECEDENTS: The attached "STJ.pptx" contains the current STJ Advisors visual standard. Match the closest relevant precedent for investment themes, company overviews, financials, and process pages. Prefer its title/subtitle hierarchy, left-edge logo, footnote band, and navy/teal/orange palette over inventing a generic layout.

CRITICAL FONT RULE: You MUST include \`fontFamily\` on EVERY shape you create or modify — even when editing existing text. This OVERRIDES the general rule about matching the existing presentation's fonts. STJ uses Nexa throughout:
- Titles / headings / page numbers: \`"Nexa Bold"\`
- Body, bullets, tables, charts, footnotes: \`"NexaRegular"\`
- Subtitles: \`"Nexa Light"\`
NEVER omit fontFamily when STJ style is active. If a shape already has the wrong font, include fontFamily to correct it.

### Color palette (from the STJ master colorScheme "STJ Advisors - Colour Theme")
Use ONLY these STJ brand colors. Do NOT use arbitrary hex values.

**Theme mapping:**
- Text1 / dark1: Black #${STJ_COLORS.ink}
- Text2 / dark2: Teal #${STJ_COLORS.teal}
- Background1 / light1: White #${STJ_COLORS.white}
- Background2 / light2: Pale teal #${STJ_COLORS.paleTeal}
- Accent1: Navy #${STJ_COLORS.navy}
- Accent2: Pale navy #${STJ_COLORS.paleNavy}
- Accent3: Orange #${STJ_COLORS.orange}
- Accent4: Pale orange #${STJ_COLORS.paleOrange}
- Accent5: White #${STJ_COLORS.white}
- Accent6: Black #${STJ_COLORS.ink}

**Usage:**
- **Primary brand / emphasis**: Navy #${STJ_COLORS.navy}
- **Secondary emphasis**: Teal #${STJ_COLORS.teal}
- **Tertiary / highlight**: Orange #${STJ_COLORS.orange}
- **Text / ink**: Black #${STJ_COLORS.ink}
- **Lines & borders**: Gray #${STJ_COLORS.ruleGray}
- **Background / shading**: Pale navy-tint #${STJ_COLORS.altRow}
- **Chart series palette** (in order): ${STJ_CHART_SERIES_PALETTE.map((c) => `#${c}`).join(", ")}
  Lead with navy (#${STJ_COLORS.navy}) as the first/primary series color.
- Do not use the scheme hyperlink white for body links; use navy or teal.

### Typography (authoritative — from the STJ master / "2_Title Slide" layout)
Slide dimensions: 960 × 540 pt (16:9).

**Placeholder positions:**
- **Title**: left=47.2, top=16.1, width=893.6, height=33.8 — "Nexa Bold" 22pt, black (#${STJ_COLORS.ink}) or navy, left-aligned
- **Subtitle**: left=47.2, top=42.0, width=893.6, height=21.8 — "Nexa Light" 16pt
- **Content area**: starts at left=47.2, top=63.8
- **Footnotes**: left=41.2, top=508.8, width=877.5, height=20.0 — "NexaRegular" 6pt
- **Page number**: right of the footnote band around left=935.5, top=504.6 — "Nexa Bold" 9pt

**Content text hierarchy ("NexaRegular", black #${STJ_COLORS.ink}, ~10-12pt body):**
- Keep body text ~10-12pt; sub-levels smaller. Sentence case, single-spaced, left-justified.
- For an emphasized section heading, use "Nexa Bold" or the navy accent; bullets otherwise inherit black text.

### Charts (STJ style)
- Font: "NexaRegular", minimum 8pt
- **Primary data color**: navy (#${STJ_COLORS.navy}); multi-series uses the series palette above in order
- Data labels: positioned outside bars/columns (inside for stacked)
- Gridlines: minimal — none by default; only when no data labels are shown
- Legend: bottom-center placement (only when needed for multi-series)
- No 3D effects, gradients, or graphic fills

### Tables (STJ style)
- Header row: navy (#${STJ_COLORS.navy}) fill with white text, "Nexa Bold"
- Body: white fill, black "NexaRegular" text (10pt)
- Alternate-row shading: pale navy-tint (#${STJ_COLORS.altRow})
- Borders: thin gray (#${STJ_COLORS.ruleGray}); align numeric columns consistently

### Shapes & layout (STJ style)
- Separate content with SHARP rectangular blocks — NOT rounded shapes
- Use thin gray (#${STJ_COLORS.ruleGray}) or teal (#${STJ_COLORS.teal}) rule lines for separation when needed
- Connectors: solid gray (#${STJ_COLORS.ruleGray}) lines
- Page numbers / footnotes: 6-9pt, "NexaRegular" / "Nexa Bold"
- Prefer layout "2_Title Slide" for body slides (title + subtitle + footnote + page number). "1_Title Slide" and "5_Title Slide" are full-bleed covers.

**TEXT FITTING RULE**: Keep labels and item descriptions SHORT (max 6-8 words each). Text boxes are narrow; long descriptions overflow. Use concise labels, not full sentences.
`;
